import type { AddressRecord, BookingProposalItemRecord } from '@navio/nsdk';
import { BookingProposalType, DeliveryWindowOutputRecord } from '@navio/nsdk';
import { BookingAddress } from 'noddi-types';
import type { ProposalAnalyticsProps, StationarySlotHold } from 'noddi-web';
import { createContext, useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

/** Who the proposal is addressed to, as its landing page already shows it. */
export interface ProposalRecipient {
  contactName: string;
  contactPhoneNumber?: string;
  /** Set for an organization proposal: any member of it may book. */
  organizationName?: string;
}

interface BookingProposalStoreInitProps {
  address?: AddressRecord;
  bookingProposalItems: BookingProposalItemRecord[];
  slug: string;
  id: number;
  type: BookingProposalType;
  serviceDepartmentId: number;
  /** The group the proposal was made for — the workspace a login has to land in. */
  userGroupId: number;
  bookedForUserId: number;
  recipient: ProposalRecipient;
  /**
   * Funnel dimensions (brand, department, customer type, channel) for every step.
   * Held here because the address, login and builder steps never fetch the
   * proposal, and an event without the breakdown is an event outside the funnel.
   */
  analytics: ProposalAnalyticsProps;
}

export type BookingProposalServerState = Omit<BookingProposalStoreInitProps, 'address'>;

interface BookingProposalState extends Omit<BookingProposalStoreInitProps, 'address'> {
  address?: AddressRecord | BookingAddress;
  actions: {
    clearStore: () => void;
    setAddress: (address?: BookingAddress) => void;
    setBookingTimeWindow: (bookingTimeWindow?: DeliveryWindowOutputRecord) => void;
    setComment: (comment: string) => void;
    setPinLocation: (pinLocation?: { lat: number; lng: number }) => void;
    setStationaryReservation: (reservation: StationarySlotHold | null) => void;
    syncProposal: (proposal: BookingProposalServerState) => void;
  };
  hasHydrated?: boolean;
  bookingTimeWindow?: DeliveryWindowOutputRecord;
  comment?: string;
  pinLocation?: { lat: number; lng: number };
  /** Shared hold shape (same as booking flow / partner). */
  stationaryReservation: StationarySlotHold | null;
}

export type BookingProposalStore = ReturnType<typeof createBookingProposalStore>;

export const createBookingProposalStore = (initProps: BookingProposalStoreInitProps) => {
  const storageKey = `booking-proposal-store-v3-${initProps.id}`;
  sessionStorage.removeItem(`booking-proposal-store-v2-${initProps.id}`);
  const draftInitialState = {
    address: initProps.address,
    bookingTimeWindow: undefined,
    comment: '',
    pinLocation: undefined,
    stationaryReservation: null
  };

  return createStore<BookingProposalState>()(
    devtools(
      persist(
        (set) => ({
          ...initProps,
          ...draftInitialState,
          actions: {
            clearStore: () => {
              set(draftInitialState);
              sessionStorage.removeItem(storageKey);
            },
            setAddress: (address) =>
              set((state) =>
                state.address?.id === address?.id
                  ? { address }
                  : { address, bookingTimeWindow: undefined, pinLocation: undefined }
              ),
            setBookingTimeWindow: (bookingTimeWindow) => set({ bookingTimeWindow }),
            setComment: (comment) => set({ comment }),
            setPinLocation: (pinLocation) => set({ pinLocation }),
            setStationaryReservation: (reservation) => set({ stationaryReservation: reservation }),
            syncProposal: (proposal) =>
              set((state) => ({
                ...proposal,
                // A background refetch rebuilds these dimensions into a new object
                // with the same values. Step tracking keys off the object identity,
                // so handing consumers a fresh one would count the screen twice.
                analytics: shallow(state.analytics, proposal.analytics) ? state.analytics : proposal.analytics
              }))
          }
        }),
        {
          // v3 persists customer input only; API proposal records are synchronized from React Query.
          name: storageKey,
          storage: createJSONStorage(() => sessionStorage),
          partialize: (state) => ({
            address: state.address,
            bookingTimeWindow: state.bookingTimeWindow,
            comment: state.comment,
            pinLocation: state.pinLocation,
            stationaryReservation: state.stationaryReservation
          })
        }
      )
    )
  );
};

export const BookingProposalContext = createContext<BookingProposalStore | null>(null);

function useBookingProposalContext<T>(selector: (state: BookingProposalState) => T): T {
  const store = useContext(BookingProposalContext);
  if (!store) {
    throw new Error('Missing BookingProposalContext.Provider in the tree');
  }
  return useStore(store, selector);
}

export const useBookingProposalAddress = () => useBookingProposalContext((state) => state.address);
export const useBookingProposalSlug = () => useBookingProposalContext((state) => state.slug);
export const useBookingProposalType = () => useBookingProposalContext((state) => state.type);
export const useBookingProposalTimeWindow = () => useBookingProposalContext((state) => state.bookingTimeWindow);
export const useBookingProposalItems = () => useBookingProposalContext((state) => state.bookingProposalItems);
export const useBookingProposalId = () => useBookingProposalContext((state) => state.id);
export const useBookingProposalAnalytics = () => useBookingProposalContext((state) => state.analytics);
export const useBookingProposalServiceDepartmentId = () =>
  useBookingProposalContext((state) => state.serviceDepartmentId);
export const useBookingProposalContactUserId = () => useBookingProposalContext((state) => state.bookedForUserId);
export const useBookingProposalUserGroupId = () => useBookingProposalContext((state) => state.userGroupId);
export const useBookingProposalRecipient = () => useBookingProposalContext((state) => state.recipient);
export const useBookingProposalComment = () => useBookingProposalContext((state) => state.comment);
export const useBookingProposalPinLocation = () => useBookingProposalContext((state) => state.pinLocation);
export const useBookingProposalStationaryReservation = () =>
  useBookingProposalContext((state) => state.stationaryReservation);
export const useBookingProposalActions = () => useBookingProposalContext((state) => state.actions);
